import type { ProviderMetadata, ReasoningUIPart } from "ai";
import { isRecord } from "../../../common/utils/isRecord";

export type ReasoningTime = { seconds: number };

const LANGFAST_NAMESPACE = "langfast";

function parseReasoningTime(value: unknown): ReasoningTime | undefined {
  if (!isRecord(value) || typeof value.seconds !== "number" || !Number.isFinite(value.seconds)) {
    return undefined;
  }
  return { seconds: value.seconds };
}

/**
 * Read langfast.reasoning_time from AI SDK providerMetadata.
 */
function getReasoningTimeFromProviderMetadata(
  providerMetadata: ProviderMetadata | undefined,
): ReasoningTime | undefined {
  if (!providerMetadata) return undefined;
  const langfast = providerMetadata[LANGFAST_NAMESPACE];
  if (!isRecord(langfast)) return undefined;
  return parseReasoningTime(langfast.reasoning_time);
}

/**
 * Read reasoning_time from a persisted ModelMessage content part.
 */
function getReasoningTimeFromContentPart(part: Record<string, unknown>): ReasoningTime | undefined {
  return parseReasoningTime(part.reasoning_time);
}

/**
 * Build providerMetadata carrying langfast.reasoning_time (merges existing).
 */
function withReasoningTimeProviderMetadata(
  reasoning_time: ReasoningTime | undefined,
  existing?: ProviderMetadata,
): ProviderMetadata | undefined {
  if (!reasoning_time && !existing) return undefined;
  if (!reasoning_time) return existing;

  const existingLangfast = isRecord(existing?.[LANGFAST_NAMESPACE])
    ? existing[LANGFAST_NAMESPACE]
    : {};

  return {
    ...existing,
    [LANGFAST_NAMESPACE]: {
      ...existingLangfast,
      reasoning_time,
    },
  };
}

function getReasoningTimeSeconds(part: ReasoningUIPart): number | undefined {
  return getReasoningTimeFromProviderMetadata(part.providerMetadata)?.seconds;
}

export {
  getReasoningTimeFromContentPart,
  getReasoningTimeFromProviderMetadata,
  getReasoningTimeSeconds,
  withReasoningTimeProviderMetadata,
};
